"use client";;
import {
  Children,
  createContext,
  isValidElement,
  use,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";
import {
  LabelList as RechartsLabelList,
  Pie as RechartsPie,
  PieChart as RechartsPieChart,
  Sector,
} from "recharts";
import { ChartTooltip, ChartTooltipContent } from "@/components/evilcharts/ui/recharts-tooltip";
import { ChartContainer, getColorsCount, LoadingIndicator } from "@/components/evilcharts/ui/recharts-chart";
import { ChartLegend, ChartLegendContent } from "@/components/evilcharts/ui/recharts-legend";
import { ChartBackground } from "@/components/evilcharts/ui/recharts-background";
import { motion, useReducedMotion } from "motion/react";

// Constants
const LOADING_SECTORS = 5;
const LOADING_ANIMATION_DURATION = 2000; // full loading cycle duration in milliseconds
const DEFAULT_INNER_RADIUS = 0;
const DEFAULT_OUTER_RADIUS = "80%";
const DEFAULT_CORNER_RADIUS = 0;
const DEFAULT_PADDING_ANGLE = 0;
const DEFAULT_START_ANGLE = 0;
const DEFAULT_END_ANGLE = 360;
// Stable empty-array reference so the `glowingSectors` default doesn't change every render
const EMPTY_GLOWING_SECTORS = [];
// Stable references — a fresh object/function each render would restart the pie animation (see <Pie />)
const CLICKABLE_STYLE = { cursor: "pointer" };
const renderLoadingSector = (props) => <AnimatedLoadingSector {...props} />;

const PieChartContext = createContext(null);

// Reads the chart context, throwing a helpful error when used outside <EvilPieChart />
function usePieChart() {
  const context = use(PieChartContext);

  if (!context) {
    throw new Error(
      "Pie chart parts (<Pie />, <Tooltip />, …) must be used within <EvilPieChart />",
    );
  }

  return context;
}

/**
 * ⚠️ STABILNÍ PROPS (B-PLAN, 29. 9. 2026): `chartProps`, `config` a props části (radialBarProps/pieProps)
 *    předávej jako konstantu modulu nebo přes useMemo. Inline objekt = nová reference při každém
 *    vykreslení rodiče = graf se znovu roztočí (hlídá evilcharts/__tests__/obloukJednaAnimace.test.js).
 * Root of the composible pie chart. Owns the data, the shared context, and the
 * loading skeleton. Everything visual — the pie itself, tooltip, legend, and an
 * optional background — is composed as children, so a consumer renders exactly
 * the parts they need.
 */
export function EvilPieChart(
  {
    config,
    data,
    dataKey,
    nameKey,
    children,
    className,
    chartProps,
    defaultSelectedSector = null,
    onSelectionChange,
    isLoading = false
  }
) {
  const [selectedSector, setSelectedSector] = useState(defaultSelectedSector);

  // Updates selection state and notifies the parent with the sector's value
  const selectSector = useCallback(
    (sectorName) => {
      setSelectedSector(sectorName);

      if (sectorName === null) {
        onSelectionChange?.(null);
        return;
      }

      const selectedItem = data.find((item) => (item[nameKey]) === sectorName);

      if (selectedItem) {
        onSelectionChange?.({ dataKey: sectorName, value: selectedItem[dataKey] });
      }
    },
    [data, dataKey, nameKey, onSelectionChange],
  );

  const contextValue = useMemo(() => ({
    config,
    data,
    dataKey,
    nameKey,
    isLoading,
    selectedSector,
    selectSector,
  }), [config, data, dataKey, nameKey, isLoading, selectedSector, selectSector]);

  return (
    <PieChartContext value={contextValue}>
      <ChartContainer className={className} config={config}>
        <LoadingIndicator isLoading={isLoading} />
        <RechartsPieChart id="evil-charts-pie-chart" accessibilityLayer {...chartProps}>
          {children}
        </RechartsPieChart>
      </ChartContainer>
    </PieChartContext>
  );
}

/**
 * The pie series. Self-contained: it generates its own radial color gradients
 * and glow filters under a unique id, so any number of pies — each with its own
 * shape and clickability — can live on one page without style collisions. While
 * the chart is loading it renders an animated skeleton in place of the data.
 * Compose <Label /> inside it to draw labels on each sector.
 */
function Pie({
  variant = "gradient",
  innerRadius = DEFAULT_INNER_RADIUS,
  outerRadius = DEFAULT_OUTER_RADIUS,
  cornerRadius = DEFAULT_CORNER_RADIUS,
  paddingAngle = DEFAULT_PADDING_ANGLE,
  startAngle = DEFAULT_START_ANGLE,
  endAngle = DEFAULT_END_ANGLE,
  isClickable = false,
  glowingSectors = EMPTY_GLOWING_SECTORS,
  children,
  pieProps
}) {
  const { config, data, dataKey, nameKey, isLoading, selectedSector, selectSector } = usePieChart();
  const id = useId().replace(/:/g, ""); // unique id scopes this pie's style defs
  /* B-PLAN: Recharts 3.6 systémové „omezit pohyb" ignoruje — koláč by se točil
   * i lidem, kteří si pohyb vypnuli. Řídíme to sami (CLAUDE.md „Grafy a pohyb"). */
  const shouldReduceMotion = useReducedMotion();

  /* B-PLAN (nález kontroly 29. 9. 2026): koláč se NESMÍ roztočit znovu, když se
   * jen překreslí rodič. Recharts 3.6 Pie skládá při KAŽDÉM svém vykreslení nový
   * objekt props pro animaci (useAnimationId porovnává referenci) → každé
   * vykreslení = nový vstup. Nové pole dat a inline `shape`/`onClick` ho
   * vykreslovaly pokaždé. Proto: stabilní data, callbacky i popisek a celý
   * ELEMENT v useMemo — React pak podstrom Recharts při stejných vstupech
   * nepřekreslí. Hlídá components/evilcharts/__tests__/obloukJednaAnimace.test.js. */
  const preparedData = useMemo(
    () => data.map((item) => ({
      ...item,
      fill: `url(#${id}-colors-${item[nameKey]})`,
    })),
    [data, nameKey, id],
  );

  const handleClick = useCallback(
    (_, index) => {
      if (!isClickable) return;
      const clickedName = data[index]?.[nameKey];
      // Clicking the selected sector clears the selection, otherwise selects it
      selectSector(selectedSector === clickedName ? null : clickedName);
    },
    [isClickable, data, nameKey, selectSector, selectedSector],
  );

  const renderShape = useCallback(
    (props) => {
      const sectorName = data[props.index ?? 0]?.[nameKey];
      const isGlowing = glowingSectors.includes(sectorName);
      const isDimmed = isClickable && selectedSector !== null && selectedSector !== sectorName;

      return (
        <Sector
          {...props}
          fill={`url(#${id}-colors-${sectorName})`}
          filter={isGlowing ? `url(#${id}-glow-${sectorName})` : undefined}
          stroke={paddingAngle < 0 ? "var(--surface)" : "none"}
          strokeWidth={paddingAngle < 0 ? 5 : 0}
          opacity={isDimmed ? 0.15 : 1}
          className="transition-opacity duration-200"
        />
      );
    },
    [data, nameKey, glowingSectors, isClickable, selectedSector, id, paddingAngle],
  );

  /* `<Label />` se čte z children; children jsou při každém překreslení rodiče
   * nový element, proto se popisek porovnává podle svých props, ne reference. */
  const labelChild = findLabel(children);
  const labelDataKey = labelChild?.props?.dataKey;
  const labelListProps = labelChild?.props?.labelListProps;
  const hasLabel = Boolean(labelChild);
  const label = useMemo(
    () => (hasLabel ? buildLabel(labelDataKey, labelListProps, dataKey) : null),
    [hasLabel, labelDataKey, labelListProps, dataKey],
  );

  const pie = useMemo(
    () => (
      <RechartsPie
        data={preparedData}
        dataKey={dataKey}
        nameKey={nameKey}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        cornerRadius={cornerRadius}
        paddingAngle={paddingAngle}
        startAngle={startAngle}
        endAngle={endAngle}
        strokeWidth={0}
        isAnimationActive={!shouldReduceMotion}
        animationDuration={700}
        animationEasing="cubic-bezier(0.22, 1, 0.36, 1)"
        style={isClickable ? CLICKABLE_STYLE : undefined}
        onClick={handleClick}
        shape={renderShape}
        {...pieProps}
      >
        {label}
      </RechartsPie>
    ),
    [preparedData, dataKey, nameKey, innerRadius, outerRadius, cornerRadius, paddingAngle, startAngle, endAngle,
      shouldReduceMotion, isClickable, handleClick, renderShape, pieProps, label],
  );

  if (isLoading) {
    return (
      <RechartsPie
        data={LOADING_PIE_DATA}
        dataKey="value"
        nameKey="name"
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        cornerRadius={cornerRadius}
        paddingAngle={paddingAngle}
        startAngle={startAngle}
        endAngle={endAngle}
        strokeWidth={0}
        isAnimationActive={false}
        shape={renderLoadingSector}
      />
    );
  }

  return (
    <>
      {pie}
      <defs>
        <RadialColorGradient id={id} config={config} variant={variant} />
        {glowingSectors.length > 0 && <GlowFilter id={id} glowingSectors={glowingSectors} />}
      </defs>
    </>
  );
}

/**
 * Declares per-sector labels for the <Pie /> it is composed inside. It renders
 * nothing on its own — the parent <Pie /> reads its props and wires them into a
 * Recharts LabelList drawn over the sectors.
 */
const Label = () => null;

/**
 * The hover tooltip. Hidden automatically while the chart is loading.
 */
function Tooltip({
  variant,
  roundness,
  defaultIndex
}) {
  const { isLoading, nameKey } = usePieChart();

  if (isLoading) return null;

  return (
    <ChartTooltip
      defaultIndex={defaultIndex}
      content={
        <ChartTooltipContent nameKey={nameKey} hideLabel roundness={roundness} variant={variant} />
      }
    />
  );
}

/**
 * The sector legend. When `isClickable` is set, each entry toggles selection of
 * its sector, driving the shared selection state read by the <Pie />.
 */
function Legend({
  variant,
  align = "center",
  verticalAlign = "bottom",
  isClickable = false
}) {
  const { nameKey, selectedSector, selectSector } = usePieChart();

  return (
    <ChartLegend
      verticalAlign={verticalAlign}
      align={align}
      content={
        <ChartLegendContent
          selected={selectedSector}
          onSelectChange={selectSector}
          isClickable={isClickable}
          nameKey={nameKey}
          variant={variant}
        />
      }
    />
  );
}

/**
 * An optional decorative pattern drawn behind the pie. Compose it before the
 * <Pie /> so it sits underneath the sectors.
 */
function Background({
  variant = "dots"
}) {
  return <ChartBackground variant={variant} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Label helper
// ─────────────────────────────────────────────────────────────────────────────

// Finds the <Label /> among a pie's children (the last one wins, as before)
const findLabel = (children) => {
  let found = null;

  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === Label) found = child;
  });

  return found;
};

// Builds the Recharts LabelList element drawn over the sectors
const buildLabel = (dataKey, labelListProps, valueKey) => (
  <RechartsLabelList
    dataKey={dataKey ?? valueKey}
    stroke="none"
    fontSize={12}
    fontWeight={500}
    fill="currentColor"
    className="fill-surface"
    {...labelListProps}
  />
);

/**
 * Radial-style color gradients, one per sector. Each sector's fill paints from
 * the gradient that matches its name, supporting both single and multi-color
 * config entries.
 */
const RadialColorGradient = ({
  id,
  config
}) => {
  return (
    <>
      {Object.entries(config).map(([sectorKey, sectorConfig]) => {
        const colorsCount = getColorsCount(sectorConfig);

        return (
          <linearGradient
            key={`${id}-colors-${sectorKey}`}
            id={`${id}-colors-${sectorKey}`}
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            {colorsCount === 1 ? (
              <>
                <stop offset="0%" stopColor={`var(--color-${sectorKey}-0)`} />
                <stop offset="100%" stopColor={`var(--color-${sectorKey}-0)`} />
              </>
            ) : (
              Array.from({ length: colorsCount }, (_, index) => {
                const offset = `${(index / (colorsCount - 1)) * 100}%`;
                return (
                  <stop
                    key={offset}
                    offset={offset}
                    stopColor={`var(--color-${sectorKey}-${index}, var(--color-${sectorKey}-0))`}
                  />
                );
              })
            )}
          </linearGradient>
        );
      })}
    </>
  );
};

/** Soft outer-glow SVG filter, one per glowing sector. */
const GlowFilter = ({
  id,
  glowingSectors
}) => {
  return (
    <>
      {glowingSectors.map((sectorName) => (
        <filter
          key={`${id}-glow-${sectorName}`}
          id={`${id}-glow-${sectorName}`}
          x="-100%"
          y="-100%"
          width="300%"
          height="300%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.5 0"
            result="glow"
          />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      ))}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────────────────────────────────────

// Equal-sized sectors used to render the circular pulsing loading skeleton
const LOADING_PIE_DATA = Array.from({ length: LOADING_SECTORS }, (_, i) => ({
  name: `loading${i}`,
  value: 100 / LOADING_SECTORS,
}));

/**
 * A single skeleton sector shown while the chart is loading. Each sector pulses
 * with a staggered delay, producing a wave that travels around the pie.
 */
const AnimatedLoadingSector = (props) => {
  const { index = 0, ...sectorProps } = props;

  // Staggered delay so the pulse sweeps around the circle
  const delay = (index / LOADING_SECTORS) * (LOADING_ANIMATION_DURATION / 1000);

  return (
    <motion.g
      initial={{ opacity: 0.15 }}
      animate={{ opacity: [0.15, 0.5, 0.15] }}
      transition={{
        duration: LOADING_ANIMATION_DURATION / 1000,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <Sector {...sectorProps} fill="currentColor" />
    </motion.g>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Static parts attached to the root
// ─────────────────────────────────────────────────────────────────────────────

EvilPieChart.Pie = Pie;
EvilPieChart.Label = Label;
EvilPieChart.Tooltip = Tooltip;
EvilPieChart.Legend = Legend;
EvilPieChart.Background = Background;
